import { Controller } from '@hotwired/stimulus';

// The panel's client half: stamp the page url onto each question and keep the
// list scrolled to the newest bubble (replies arrive later, over the cable).
// Opening and closing is the native popover's job — `popovertarget` on the
// bar button (signed in) or the corner bubble (public), Esc and click-outside
// for free. A browser without popover gets the same by hand (shim).
// Registered as "help" by registerJane (register.js).
export default class extends Controller {
  static targets = ['panel', 'messages', 'form', 'input', 'url', 'bubble', 'oops', 'consent'];

  connect() {
    this.observer = new MutationObserver(() => this.scroll());
    this.observer.observe(this.messagesTarget, { childList: true });
    // A card has no panel to open: the first focus inside it is the reach.
    this.reach = () => this.listen();
    this.element.addEventListener('focusin', this.reach, { once: true });
    if (this.hasPanelTarget && !('popover' in HTMLElement.prototype)) this.shim();
  }

  disconnect() {
    this.observer?.disconnect();
    this.element.removeEventListener('focusin', this.reach);
    document.removeEventListener('click', this.shimClick);
    document.removeEventListener('keydown', this.shimKey);
  }

  // Chrome before 114 has no popover, and 109 is the last Chrome Windows 7/8
  // get. There the panel renders open over the page, and no button can shut
  // it (APAE Arujá's /scan, 19/10). Hide it and drive it by hand: the same
  // three methods, the same `toggle` event, Esc and click-outside.
  shim() {
    const panel = this.panelTarget;
    const set = (open) => {
      if (open === !panel.hidden) return;
      panel.hidden = !open;
      const event = new Event('toggle');
      event.newState = open ? 'open' : 'closed';
      panel.dispatchEvent(event);
    };
    panel.hidden = true;
    panel.style.zIndex = '1000';
    panel.showPopover = () => set(true);
    panel.hidePopover = () => set(false);
    panel.togglePopover = () => set(panel.hidden);
    this.shimClick = (e) => {
      const button = e.target.closest?.('[popovertarget="fire-help"]');
      if (button) set(button.getAttribute('popovertargetaction') === 'hide' ? false : panel.hidden);
      else if (!panel.contains(e.target)) set(false);
    };
    this.shimKey = (e) => e.key === 'Escape' && set(false);
    document.addEventListener('click', this.shimClick);
    document.addEventListener('keydown', this.shimKey);
  }

  // The popover's own `toggle` event, both directions — only the opening one
  // has anything to do.
  opened(event) {
    const open = event.newState === 'open';
    if (this.hasBubbleTarget) this.bubbleTarget.setAttribute('aria-expanded', open);
    if (!open) return;

    this.listen();
    this.place();
    this.urlTarget.value = window.location.href;
    this.scroll();
    this.inputTarget.focus();
  }

  // The first reach for help: only now does the page's empty ear frame load
  // the cable subscription (Jane::Help#ear). Once, from the panel's first open or a card's first focus.
  listen() {
    const ear = document.getElementById('fire-help-ear');
    if (ear && !ear.getAttribute('src')) ear.setAttribute('src', ear.dataset.url);
  }

  // The app's one yes landed (jane/help/consent): the line goes, the composer
  // the server kept hidden opens.
  agreed(event) {
    if (!event.detail.success) return;

    this.consentTarget.remove();
    this.formTarget.hidden = false;
    this.inputTarget.focus();
  }

  // "Not now": the AI stays off. The panel shuts; a card, which has no panel,
  // drops its line.
  declined() {
    if (this.hasPanelTarget) this.panelTarget.hidePopover();
    else this.consentTarget.hidden = true;
  }

  // A 5xx is not Turbo's to paint over the whole page: the panel says it.
  refused(event) {
    if (event.detail.fetchResponse.serverError) event.preventDefault();
  }

  // Not sent: a 5xx or no network gets a line, the words stay in the field.
  // A 422 (a blank question) is refused quietly.
  sent(event) {
    const { success, fetchResponse } = event.detail;
    if (!success) {
      if (this.hasOopsTarget && (!fetchResponse || fetchResponse.serverError))
        this.messagesTarget.append(this.oopsTarget.content.cloneNode(true));
      return;
    }

    this.formTarget.reset();
    this.urlTarget.value = window.location.href;
    this.inputTarget.focus();
  }

  // The card hangs off the bubble, wherever the host parked it (Jane.help_at):
  // above it, on its side of the screen. On a phone it takes the width.
  // No bubble (the bar's sheet) — the inline style already said where.
  place() {
    if (!this.hasBubbleTarget) return;

    const r = this.bubbleTarget.getBoundingClientRect();
    const s = this.panelTarget.style;
    const gap = 12;
    s.top = 'auto';
    s.bottom = `${window.innerHeight - r.top + gap}px`;
    if (window.innerWidth < 640) {
      Object.assign(s, { left: '0.75rem', right: 'auto', width: 'calc(100vw - 1.5rem)' });
    } else if (r.left + r.width / 2 > window.innerWidth / 2) {
      Object.assign(s, { left: 'auto', right: `${window.innerWidth - r.right}px`, width: '' });
    } else {
      Object.assign(s, { left: `${r.left}px`, right: 'auto', width: '' });
    }
  }

  scroll() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }
}
